import { HttpClient, HttpResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { deserializeProject } from "@domain/project/project-deserializer";
import type { ScopedProject } from "@domain/project/project-guards";
import { isBusinessScope, isFgdsProject } from "@domain/project/project-guards";
import { BusinessScope } from "@lib/constants/business-scope";
import { ProjectBusinessScopeDto } from "@lib/dtos/project/project-business-scope.dto";
import { ErrorReportingService } from "@utils/error-reporting.service";
import { catchError, map, Observable, of, ReplaySubject, throwError } from "rxjs";

import { ProjectResponseDto } from "@domain/project/api/project-response.dto";
import { UserDataDto } from "@domain/project/api/user-data.dto";
import { CloudProjectMetadata } from "@domain/project/cloud-project-metadata";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { ProjectFile } from "@domain/project/project-file";
import { ProjectImageData } from "@domain/project/project-image.service";
import { environment } from "@environments/environment";
import { ObservableInstanceMapper } from "@utils/observable-instance-mapper";
import { instanceToPlain, plainToInstance } from "class-transformer";
import { ProjectImageMetadata } from "../project-image-metadata";

@Injectable({
  providedIn: "root",
})
export class ProjectApiService {
  private http = inject(HttpClient);
  private errorReportingService = inject(ErrorReportingService);

  private readonly _projects$ = new ReplaySubject<ScopedProject[]>(1);
  public static PROJECTS_URL = environment.apiUrl + "/projects";

  exportProjectToCloud(
    project: ScopedProject,
    metadata?: CloudProjectMetadata,
    forceUpdate?: boolean,
  ): Observable<ProjectResponseDto> {
    return this.createExportToCloudRequest(project, metadata, forceUpdate).pipe(
      map((response: unknown) => {
        const rawResponse = (response ?? {}) as {
          project?: Record<string, unknown>;
          metadata?: Record<string, unknown>;
        };
        const projectResponse = plainToInstance(ProjectResponseDto, response);
        const metadataResponse =
          rawResponse.metadata && typeof rawResponse.metadata === "object"
            ? plainToInstance(CloudProjectMetadata, rawResponse.metadata)
            : undefined;

        const fallbackScope = isBusinessScope(metadata?.businessScope)
          ? metadata.businessScope
          : isFgdsProject(project)
            ? BusinessScope.FGDS
            : BusinessScope.MONITORING;
        const responseScope = isBusinessScope(metadataResponse?.businessScope) ? metadataResponse.businessScope : fallbackScope;

        projectResponse.project =
          rawResponse.project && typeof rawResponse.project === "object"
            ? deserializeProject(rawResponse.project, responseScope)
            : undefined;
        projectResponse.metadata = metadataResponse;
        return projectResponse;
      }),
    );
  }

  getMetadata(cloudId: string): Observable<CloudProjectMetadata | undefined> {
    return ObservableInstanceMapper.valueToInstance(
      this.http.get(`${ProjectApiService.PROJECTS_URL}/${cloudId}/metadata`),
      CloudProjectMetadata,
    ).pipe(
      map((metadata) => {
        if (metadata.name) {
          metadata.displayName = metadata.name;
        }
        return metadata;
      }),
      catchError((error) => {
        if (error.status === 404) {
          return of(undefined);
        }
        return throwError(() => error);
      }),
    );
  }

  inviteCollaborators(emailAddresses: string[], cloudId: string | undefined): Observable<any> {
    if (!cloudId) {
      throw new Error("cloudId is undefined");
    }
    const invitedAt: Date = new Date();
    const payload = {
      invitedAt: invitedAt,
      collaboratorMailAddresses: emailAddresses,
    };
    return this.http.post(`${ProjectApiService.PROJECTS_URL}/${cloudId}/collaboration/invite`, payload).pipe(
      map((response) => {
        return response;
      }),
    );
  }

  deleteProjectFromCloud(cloudId: string): Observable<any> {
    return this.http.delete(`${ProjectApiService.PROJECTS_URL}/${cloudId}`);
  }

  completeInvitation(cloudId: string, invitationId: string): Observable<HttpResponse<any>> {
    return this.http.patch<any>(
      `${ProjectApiService.PROJECTS_URL}/${cloudId}/invitations/${invitationId}/confirm`,
      {},
      { observe: "response" },
    );
  }

  removeCollaborator(cloudId: string | undefined, collaboratorId: string): Observable<CloudProjectMetadata> {
    if (!cloudId) {
      throw new Error("cloudId is undefined");
    }
    return this.http
      .patch(`${ProjectApiService.PROJECTS_URL}/${cloudId}/collaboration/remove`, { collaboratorId: collaboratorId })
      .pipe(map((response) => plainToInstance(CloudProjectMetadata, response)));
  }

  getUserData(cloudId: string): Observable<UserDataDto> {
    return this.http
      .get(`${ProjectApiService.PROJECTS_URL}/${cloudId}/user`)
      .pipe(map((response) => plainToInstance(UserDataDto, response)));
  }

  deleteFile(cloudId: string, fileId: string): Observable<CloudProjectMetadata> {
    return this.http
      .delete(`${ProjectApiService.PROJECTS_URL}/${cloudId}/files/${fileId}`)
      .pipe(map((response) => plainToInstance(CloudProjectMetadata, response)));
  }

  addFiles(cloudId: string, blobs: Blob[]): Observable<CloudProjectMetadata> {
    const formData = new FormData();
    for (const blob of blobs) {
      formData.append("files", blob);
    }
    return this.http
      .post(`${ProjectApiService.PROJECTS_URL}/${cloudId}/files`, formData)
      .pipe(map((response) => plainToInstance(CloudProjectMetadata, response)));
  }

  loadProjectFiles(cloudId: string) {
    return this.http.get<any[]>(`${ProjectApiService.PROJECTS_URL}/${cloudId}/files`).pipe(
      map((response) => {
        response.map(() => {
          return plainToInstance(ProjectFile, response);
        });
        return response;
      }),
    );
  }

  downloadFile(file: ProjectFile) {
    return this.http.get(`${ProjectApiService.PROJECTS_URL}/${file.projectId}/files/${file.id}`, { responseType: "blob" });
  }

  addImages(cloudId: string, images: ProjectImageData[]): Observable<any[]> {
    const validImages = images.filter((imageData) => {
      if (imageData.blob && imageData.previewBlob && imageData.imageMetadata) {
        return true;
      }
      this.errorReportingService.captureException(new Error("Skipping image upload: missing required image data"), {
        tags: { area: "image-sync" },
        extra: { cloudId, imageId: imageData.imageMetadata?.id },
      });
      return false;
    });

    if (validImages.length === 0) {
      return of([]);
    }

    const formData = new FormData();
    validImages.forEach((imageData, index) => {
      formData.append(`image_blob_${index}`, imageData.blob!);
      formData.append(`image_blob_preview_${index}`, imageData.previewBlob!);
      formData.append(`image_metadata_${index}`, JSON.stringify(imageData.imageMetadata));
    });

    return this.http.post<any[]>(`${ProjectApiService.PROJECTS_URL}/${cloudId}/images`, formData).pipe(
      map((response: any[]) => {
        return response;
      }),
    );
  }

  deleteProjectImages(cloudId: string, ids: string[]) {
    return this.http
      .delete<any[]>(`${ProjectApiService.PROJECTS_URL}/${cloudId}/images`, {
        body: { ids },
      })
      .pipe(
        map((response: any[]) => {
          return response;
        }),
      );
  }

  loadProjectImages(cloudId: string) {
    return this.http.get<any[]>(`${ProjectApiService.PROJECTS_URL}/${cloudId}/images/metadata`).pipe(
      map((response) => {
        return response;
      }),
    );
  }

  loadProjectImage(cloudId: string, imageId: string) {
    return this.http.get(`${ProjectApiService.PROJECTS_URL}/${cloudId}/images/${imageId}`, { responseType: "blob" });
  }

  loadPreviewImage(cloudId: string, imageId: string) {
    return this.http.get(`${ProjectApiService.PROJECTS_URL}/${cloudId}/images/${imageId}/preview`, { responseType: "blob" });
  }

  updateProjectImages(cloudId: string, imageMetadata: ProjectImageMetadata[]) {
    return this.http.put<any[]>(`${ProjectApiService.PROJECTS_URL}/${cloudId}/images/metadata`, { imageMetadata }).pipe(
      map((response: any[]) => {
        return response;
      }),
    );
  }

  addFloorplans(cloudId: string, floorplans: Floorplan[]): Observable<any[]> {
    return this.http.post<any[]>(`${ProjectApiService.PROJECTS_URL}/${cloudId}/floorplans`, { floorplans }).pipe(
      map((response: any[]) => {
        return response;
      }),
    );
  }

  deleteFloorplans(cloudId: string, ids: string[]) {
    return this.http
      .delete<any[]>(`${ProjectApiService.PROJECTS_URL}/${cloudId}/floorplans`, {
        body: { ids },
      })
      .pipe(
        map((response: any[]) => {
          return response;
        }),
      );
  }

  loadProjectFloorplans(cloudId: string) {
    return this.http.get<any[]>(`${ProjectApiService.PROJECTS_URL}/${cloudId}/floorplans`).pipe(
      map((response) => {
        return response;
      }),
    );
  }

  getReadAccessProject(cloudId: string) {
    return this.http.get(`${ProjectApiService.PROJECTS_URL}/${cloudId}/read-access`);
  }

  getBusinessScopeByProjectId(cloudId: string): Observable<BusinessScope> {
    return ObservableInstanceMapper.valueToInstance(
      this.http.get(`${ProjectApiService.PROJECTS_URL}/${cloudId}/business-scope`),
      ProjectBusinessScopeDto,
    ).pipe(map((response) => response.businessScope as BusinessScope));
  }

  private createExportToCloudRequest(project: ScopedProject, metadata?: CloudProjectMetadata, forceUpdate?: boolean) {
    const payload = this.createPayload(project, metadata);
    if (project.cloudId && metadata) {
      if (forceUpdate) {
        return this.http.put(`${ProjectApiService.PROJECTS_URL}/${project.cloudId}?forceUpdate=${forceUpdate}`, payload);
      }
      return this.http.put(`${ProjectApiService.PROJECTS_URL}/${project.cloudId}`, payload);
    }
    return this.http.post(ProjectApiService.PROJECTS_URL, payload);
  }

  private createPayload(project: ScopedProject, metadata?: CloudProjectMetadata) {
    if (metadata) {
      const plainMetadata = instanceToPlain(metadata);
      return {
        project: instanceToPlain(project),
        metadata: plainMetadata,
      };
    }
    return { project: instanceToPlain(project) };
  }

  shareProjectLink(
    payload: { mailAddresses: string[]; showPrices: boolean; language?: string },
    cloudId: string,
  ): Observable<any> {
    return this.http.post(`${ProjectApiService.PROJECTS_URL}/${cloudId}/read-access`, payload);
  }

  getProjectCloudIdByLocalId(localId: string): Observable<string | undefined> {
    return this.http
      .get(`${ProjectApiService.PROJECTS_URL}/lookup/${localId}`, { responseType: "text" })
      .pipe(catchError(() => of(undefined)));
  }
}
